import { ForwardRefExoticComponent, RefAttributes, useMemo } from "react";

import { Icon, IconProps } from "@tabler/icons-react";
import { useTranslations } from "next-intl";

import Icon247 from "@/icons/content/property/24-7.svg";
import IconArea from "@/icons/content/property/area-bold.svg";
import IconCondition from "@/icons/content/property/buildings-bold.svg";
import IconBuildings from "@/icons/content/property/buildings.svg";
import IconHeating from "@/icons/content/property/heating.svg";
import IconLift from "@/icons/content/property/lift.svg";
import IconParking from "@/icons/content/property/parking.svg";
import IconFloor from "@/icons/content/property/stairs-bold.svg";
import IconStatus from "@/icons/content/property/status.svg";
import IconApartment from "@/icons/ui/buttons/home.svg";
import IconLayout from "@/icons/ui/controls/layout-grid.svg";
import IconCompletionYear from "@/icons/ui/forms/calendar.svg";
import IconLandArea from "@/icons/content/property/landAreaBig.svg"

interface ConfigType {
  name: string;
  translateValue: string;
  value: string | number | boolean;
  Icon: ForwardRefExoticComponent<IconProps & RefAttributes<Icon>>;
}

export type Characteristics = (data: any) => ConfigType[];

export const useCharacteristicsConfig: Characteristics = (data) => {
  const t = useTranslations();
  const tPropertyOptions = useTranslations("PropertyOptions");
  const tAdditional = useTranslations("AdditionalFilters");
  const tFields = useTranslations("fields");

  const access24_7_value_resolver = useMemo(() => {
    if (data?.additionalInfo && "has24_7Access" in data.additionalInfo) {
      return data.additionalInfo?.has24_7Access
        ? t("AdditionalFilters.Yes")
        : t("AdditionalFilters.No");
    }
    return false;
  }, [data, t]);

  return [
    {
      name: "projectInfo.constructionStatus",
      translateValue: t("Construction status"),
      value: data?.projectInfo?.constructionStatus
        ? tAdditional(data.projectInfo.constructionStatus)
        : false,
      Icon: IconStatus,
    },
    {
      name: "projectInfo.completionYear",
      translateValue: t("Project delivery"),
      value: data?.projectInfo?.completionYear
        ? `${data?.projectInfo?.completionYear}${data?.projectInfo?.completionQuarter ? ` (${data?.projectInfo?.completionQuarter} ${t("quarter")})` : ""}`
        : false,
      Icon: IconCompletionYear,
    },
    {
      name: "projectInfo.numberOfBuildings",
      translateValue: t("Number of buildings"),
      value: data?.projectInfo?.numberOfBuildings
        ? t("pluralization.section", {
            count: data.projectInfo.numberOfBuildings,
          })
        : false,
      Icon: IconBuildings,
    },
    {
      name: "additionalInfo.condition",
      translateValue: t("Construction technology"),
      value: data?.additionalInfo?.condition
        ? t("AdditionalOptions." + data.additionalInfo.condition)
        : false,
      Icon: IconCondition,
    },
    {
      name: "additionalInfo.floor",
      translateValue: t("Number of floors"),
      value: data?.additionalInfo?.floors
        ? String(data.additionalInfo.floors)
        : false,
      Icon: IconFloor,
    },
    {
      name: "additionalInfo.parking",
      translateValue: t("Parking"),
      value: data?.additionalInfo?.parking
        ? t("PropertyOptions." + data.additionalInfo.parking)
        : false,
      Icon: IconParking,
    },
    {
      name: "additionalInfo.elevator",
      translateValue: t("Elevator"),
      value: data?.additionalInfo?.elevator
        ? tPropertyOptions(data?.additionalInfo?.elevator)
        : false,
      Icon: IconLift,
    },
    {
      name: "projectInfo.numberOfUnits",
      translateValue: t("Number of apartments"),
      value: data?.projectInfo?.numberOfUnits?.toString() || false,
      Icon: IconLayout,
    },
    {
      name: "mainInfo.area",
      translateValue: t("Square"),
      value: (() => {
        const { areaMin, areaMax, area } = data?.mainInfo || {};

        if (areaMin && areaMax && areaMin !== areaMax) {
          return `${areaMin} – ${areaMax} ${t("m2")}`;
        }

        if (areaMin && areaMax && areaMin === areaMax) {
          return `${areaMin} ${t("m2")}`;
        }

        if (areaMin) {
          return `${t("from")} ${areaMin} ${t("m2")}`;
        }

        if (areaMax) {
          return `${t("to")} ${areaMax} ${t("m2")}`;
        }

        if (area) {
          return `${area} ${t("m2")}`;
        }
        return false;
      })(),
      Icon: IconArea,
    },
    {
      name: "mainInfo.area",
      translateValue: tFields("Land area"),
      value: data.mainInfo.landArea ? `${data.mainInfo.landArea} Ap` : false,
      Icon: IconLandArea,
    },
    {
      name: "additionalInfo.heating",
      translateValue: tAdditional("heating"),
      value: data?.additionalInfo?.heating
        ? tAdditional(data.additionalInfo.heating)
        : false,
      Icon: IconHeating,
    },
    {
      name: "additionalInfo.renovation",
      translateValue: t("Renovation"),
      value: data?.additionalInfo?.renovation
        ? t("PropertyOptions." + data.additionalInfo.renovation)
        : false,
      Icon: IconApartment,
    },
    {
      name: "additionalInfo.has24_7Access",
      translateValue: t("Access 24/7"),
      value: access24_7_value_resolver,
      Icon: Icon247,
    },
  ].filter((item) => Boolean(item.value));
};
